/* Audit Assistant — ChatProvider seam.
 *
 * The UI talks ONLY to this interface, so a real backend can be dropped
 * in later (OpenAIProvider / ClaudeProvider / OllamaProvider / …) with
 * zero changes to the components. The current implementation is a pure
 * client-side mock — no network, no LLM.
 */

import { DEFAULT_FOLLOWUPS, DEFAULT_RESPONSE, MOCK_ENTRIES } from './mockResponses';

export interface AssistantResponse {
  /** Assistant message body (see mockResponses formatting convention). */
  text: string;
  /** Suggested next questions, rendered as chips under the answer. */
  followUps: string[];
}

export interface ChatProvider {
  sendMessage(conversationId: string, message: string): Promise<AssistantResponse>;
}

/** Resolve a user message to a mock answer: exact → keyword/substring → default. */
export function resolveMockAnswer(message: string): AssistantResponse {
  const fallback: AssistantResponse = { text: DEFAULT_RESPONSE, followUps: DEFAULT_FOLLOWUPS };
  const q = message.trim().toLowerCase();
  if (!q) return fallback;

  // 1) Exact prompt match.
  const exact = MOCK_ENTRIES.find((e) => e.prompt.toLowerCase() === q);
  if (exact) return { text: exact.answer, followUps: exact.followUps };

  // 2) Keyword / substring match (either direction).
  const fuzzy = MOCK_ENTRIES.find(
    (e) =>
      q.includes(e.prompt.toLowerCase()) ||
      e.prompt.toLowerCase().includes(q) ||
      e.keywords.some((k) => q.includes(k.toLowerCase())),
  );
  if (fuzzy) return { text: fuzzy.answer, followUps: fuzzy.followUps };

  // 3) Fallback.
  return fallback;
}

/**
 * MockChatProvider — simulates an assistant with an 800–1500ms "thinking"
 * delay and static answers. `delayFn` is injectable so it can be made
 * deterministic in tests / reduced-motion contexts.
 */
export class MockChatProvider implements ChatProvider {
  private readonly delayFn: () => number;

  constructor(delayFn?: () => number) {
    // Default: 800–1500ms. Uses performance.now() as a seed so we avoid
    // Math.random() only where it would matter; here randomness is fine
    // for a demo delay.
    this.delayFn = delayFn ?? (() => 800 + Math.random() * 700);
  }

  sendMessage(_conversationId: string, message: string): Promise<AssistantResponse> {
    const answer = resolveMockAnswer(message);
    const ms = this.delayFn();
    return new Promise((resolve) => {
      setTimeout(() => resolve(answer), ms);
    });
  }
}

/** Shared default instance the hook uses unless another provider is injected. */
export const defaultChatProvider: ChatProvider = new MockChatProvider();
